const htmlMarkupPattern = /<[a-zA-Z/!?]/u;
// eslint-disable-next-line no-control-regex
const controlCharactersPattern = /^[\u0000-\u001F]+/u;
const ignoredWhitespacePattern = /[\t\r\n]/gu;
const schemePattern = /^([a-zA-Z][a-zA-Z0-9+.-]*):/u;
const portPattern = /^\d+(\/|$)/u;

/**
 * Detects tag-like markup. Mirrors the API's HTML input check, so plain text such as `a < b` passes.
 *
 * @param value - Text to check.
 * @returns True when the text contains tag-like markup.
 */
export const containsHtml = (value: string | null | undefined): boolean =>
  !!value && htmlMarkupPattern.test(value);

/**
 * Turns a user-entered URL into a link that is safe to use as an anchor href.
 *
 * Tabs and line breaks are removed first, because browsers ignore them inside a scheme (`java\tscript:x`).
 * A scheme-like prefix followed by a port is only treated as a host when it contains a dot or is `localhost`.
 *
 * @param url - Raw URL, with or without scheme.
 * @returns The http(s) URL, `https://` prefixed when the scheme is missing, or undefined when unsafe or empty.
 */
export const toSafeExternalUrl = (url: string | null | undefined): string | undefined => {
  const value = (url ?? '')
    .replace(ignoredWhitespacePattern, '')
    .trim()
    .replace(controlCharactersPattern, '');

  if (!value || containsHtml(value)) {
    return undefined;
  }

  const schemeMatch = schemePattern.exec(value);

  const isHostWithPort =
    !!schemeMatch &&
    portPattern.test(value.slice(schemeMatch[0].length)) &&
    (!!schemeMatch[1]?.includes('.') || schemeMatch[1]?.toLowerCase() === 'localhost');

  if (schemeMatch && !isHostWithPort) {
    return ['http', 'https'].includes(schemeMatch[1]?.toLowerCase() ?? '') ? value : undefined;
  }

  return `https://${value}`;
};
